'use client';

import React from 'react';

import {Button} from '@udemy-v2/react-core-components';
import {Loader} from '@udemy-v2/react-reveal-components';
import {SubscriptionStickyFooterCta} from '@/features/course-landing-page/components/sticky-footer/subscription-sticky-footer-content/subscription-sticky-footer-cta.react-component';
import {NonCatalogPPStickyFooterCta} from '@/features/course-landing-page/components/sticky-footer/non-catalog-pp-sticky-footer-content/non-catalog-pp-sticky-footer-cta.react-component';
import {TransactionalStickyFooterContent} from '@/features/course-landing-page/components/sticky-footer/transactional-sticky-footer-content/transactional-sticky-footer-content.react-component';
import {EnrolledStickyFooterContent} from '@/features/course-landing-page/components/sticky-footer/enrolled-sticky-footer-content/enrolled-sticky-footer-content.react-component';
import {EnrollmentStickyFooterContent} from '@/features/course-landing-page/components/sticky-footer/enrollment-sticky-footer-content/enrollment-sticky-footer-content.react-component';
import {GrowWithGoogleStickyFooterContent} from '@/features/course-landing-page/components/sticky-footer/grow-with-google-sticky-footer-content/grow-with-google-sticky-footer-content.react-component';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
} from '@/features/course-landing-page/context/clp-purchase-context.react-component';

import styles from './marketplace-sticky-footer-content.module.scss';

/**
 * MarketplaceStickyFooterContent component handles conditional rendering of different sticky footer content
 * based on course enrollment status, subscription availability, and loading state.
 */
export const MarketplaceStickyFooterContent: React.FC = () => {
    const {
        isCourseInAvailablePlans,
        isLoading,
        isEnrolledInCourse,
        isFree,
        isCourseIncludedInUserSubscription,
        isCourseIncludedInGrowWithGoogleSubscription,
        isEligibleForNonCatalogPPOffer,
        selectedPurchaseOption,
    } = useClpPurchaseContext();

    // Show loading state while purchase context is loading
    if (isLoading) {
        return (
            <Button className={styles['loading-button']} disabled={true}>
                <Loader color="inherit" overlay={true} />
            </Button>
        );
    }

    // Show Grow with Google content if course is included in GwG subscription
    // Note: GwG courses will be included in some PP subscriptions, so we want to be sure to prioritize the GwG CTA
    // over all other CTAs including enrollment and enrolled states.
    if (isCourseIncludedInGrowWithGoogleSubscription) {
        return <GrowWithGoogleStickyFooterContent />;
    }

    // Show enrollment content if course is free or included in subscription
    if (isCourseIncludedInUserSubscription || (isFree && !isEnrolledInCourse)) {
        return <EnrollmentStickyFooterContent />;
    }

    // Show enrolled content if user is already enrolled
    if (isEnrolledInCourse) {
        return <EnrolledStickyFooterContent />;
    }

    // Show non-catalog PP CTA if eligible and subscription is the selected purchase option
    if (
        isEligibleForNonCatalogPPOffer &&
        selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION
    ) {
        return <NonCatalogPPStickyFooterCta />;
    }

    // Show subscription CTA if course is available in plans and subscription is selected
    if (isCourseInAvailablePlans && selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION) {
        return <SubscriptionStickyFooterCta />;
    }

    // Default to transactional content for all other cases
    return <TransactionalStickyFooterContent />;
};
